// Vendor Libs
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import {noop} from 'lodash';

// Libs
import * as browser from '../lib-core/browser';

// Styles
import './overlay-container.scss';

class OverlayContainer extends Component {
    state = {
        triggerRect: {},
        overlayRect: {},
    };

    container = null;

    UNSAFE_componentWillMount() {
        window.addEventListener('resize', this.handleResize);
    }

    componentDidMount() {
        window.addEventListener('resize', this.handleResize);
        this.container.addEventListener('click', this.handleGlobalClick);
        this.setRects();
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.handleResize);
        this.container.removeEventListener('click', this.handleGlobalClick);
    }

    setRects() {
        const {triggerNode} = this.props;

        this.setState({
            triggerRect: triggerNode.getBoundingClientRect(),
            overlayRect: this.container.getBoundingClientRect(),
        });
    }

    getClass() {
        const {displayDirection} = this.props;

        return classnames('overlay-container', {
            'overlay-container_up': displayDirection === 'up',
            'overlay-container_right': displayDirection === 'right',
            'overlay-container_down': displayDirection === 'down',
            'overlay-container_left': displayDirection === 'left',
        });
    }

    getProps(positionalStyles) {
        return {
            className: this.getClass(),
            ref: container => (this.container = container),
            style: positionalStyles,
        };
    }

    // TODO: add positional styles for pointer for left and right when content overflows top / bottom of viewport
    calculatePositionalStyles = () => {
        const {displayDirection} = this.props;

        const positionDirectionMethods = {
            up: {
                top: this.calculateUpPosition,
                left: this.calculateCenterOrOverflowPosition,
                pointer: this.calculatePointerLeftPosition,
            },
            right: {
                top: this.calculateMiddlePosition,
                left: this.calculateRightPosition,
                pointer: noop,
            },
            down: {
                top: this.calculateDownPosition,
                left: this.calculateCenterOrOverflowPosition,
                pointer: this.calculatePointerLeftPosition,
            },
            left: {
                top: this.calculateMiddlePosition,
                left: this.calculateLeftPosition,
                pointer: noop,
            },
        };
        const methods = positionDirectionMethods[displayDirection];
        const left = methods.left();
        const top = methods.top();

        return {
            positionalStyles: {
                top: `${top}px`,
                left: `${left}px`,
            },
            pointerStyles: methods.pointer(left),
        };
    };

    calculatePointerLeftPosition = overlayLeft => {
        const {overlayRect} = this.state;

        const overflowDelta = this.calculateCenterPosition() - overlayLeft;

        return {
            left: `${Math.max(overlayRect.width / 2 + overflowDelta, 20)}px`,
        };
    };

    // TODO: determine if we need a check for overlay displaying out of bounds of the viewport on the top (rare edge case)
    calculateUpPosition = () => {
        const {overlayRect, triggerRect} = this.state;
        const {overlayToTriggerMargin} = this.props;

        return triggerRect.top - overlayRect.height - overlayToTriggerMargin + browser.getScrollY();
    };

    // TODO: determine if we need a check for overlay displaying out of bounds of the viewport on the bottom (rare edge case)
    calculateDownPosition = () => {
        const {triggerRect} = this.state;
        const {overlayToTriggerMargin} = this.props;

        return triggerRect.top + triggerRect.height + overlayToTriggerMargin + browser.getScrollY();
    };

    calculateRightPosition = () => {
        const {triggerRect} = this.state;
        const {overlayToTriggerMargin} = this.props;

        return triggerRect.left + triggerRect.width + overlayToTriggerMargin;
    };

    calculateLeftPosition = () => {
        const {triggerRect, overlayRect} = this.state;
        const {overlayToTriggerMargin} = this.props;

        return triggerRect.left - overlayRect.width - overlayToTriggerMargin;
    };

    calculateCenterOrOverflowPosition = () => {
        const {overlayRect} = this.state;

        const {width} = overlayRect;
        const browserWidth = browser.getWidth();
        const visualPadding = 10;
        const centerPosition = this.calculateCenterPosition();
        let leftPosition;

        if (centerPosition < 0) {
            leftPosition = visualPadding;
        } else if (centerPosition + width > browserWidth) {
            leftPosition = browserWidth - visualPadding - width;

            const containerEdge = centerPosition - width / 2 + visualPadding * 2;

            if (leftPosition < containerEdge) {
                leftPosition = containerEdge;
            }
        } else {
            leftPosition = centerPosition;
        }

        return leftPosition;
    };

    calculateCenterPosition = () => {
        const {overlayRect, triggerRect} = this.state;

        return triggerRect.left + (triggerRect.width - overlayRect.width) / 2;
    };

    calculateMiddlePosition = () => {
        const {overlayRect, triggerRect} = this.state;

        return triggerRect.top + triggerRect.height / 2 - overlayRect.height / 2 + browser.getScrollY();
    };

    handleGlobalClick = event => {
        if (!(event.target.tagName === 'A' || event.target.tagName === 'BUTTON')) {
            event.stopPropagation();
        }
    };

    handleResize = () => {
        this.setRects();
    };

    handleContainerClick(event) {
        event.preventDefault();
    }

    renderOverlayPointer(pointerStyles) {
        const {disablePointer} = this.props;

        let pointer = null;

        if (!disablePointer) {
            pointer = <div className="overlay-container--pointer" style={pointerStyles} />;
        }

        return pointer;
    }

    render() {
        const {overlayRenderer} = this.props;

        const {pointerStyles, positionalStyles} = this.calculatePositionalStyles();

        return (
            <div {...this.getProps(positionalStyles)}>
                {overlayRenderer()}
                {this.renderOverlayPointer(pointerStyles)}
            </div>
        );
    }
}

OverlayContainer.propTypes = {
    disablePointer: PropTypes.bool,
    displayDirection: PropTypes.oneOf(['up', 'right', 'down', 'left']),
    overlayRenderer: PropTypes.func.isRequired,
    overlayToTriggerMargin: PropTypes.number,
    triggerNode: PropTypes.object.isRequired, // note this is actually a DOMNode but `PropTypes.node` doesn't work with one of those.
};

OverlayContainer.defaultProps = {
    displayDirection: 'down',
    overlayToTriggerMargin: 10,
};

export default OverlayContainer;
